"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const metrics = [
{ value: "500+", label: "Students Placed", sub: "Across 15+ countries" },
{ value: "15+", label: "Countries Covered", sub: "Global reach" },
{ value: "98%", label: "Visa Success Rate", sub: "Industry-leading success" },
{ value: "50+", label: "University Partnerships", sub: "Globally recognised" }];


const differentiators = [
{
  icon: "🤝",
  title: "Honesty First",
  desc: "We tell you what you need to hear, not what you want to hear. Honest assessments, smart targeting — that's the formula."
},
{
  icon: "🎓",
  title: "Excellence Always",
  desc: "Every application, every document, every conversation — we bring our best. Our 98% visa success rate is the result of that preparation."
},
{
  icon: "🛡️",
  title: "Partnership, Not Transactions",
  desc: "We're invested in your success long after you board that flight. Our primary loyalty is to you, the student — not commissions."
},
{
  icon: "📊",
  title: "6+ Years of Expertise",
  desc: "Over 6 years, we've built genuine relationships with universities around the world and earned a reputation we protect fiercely."
}];


export default function WhyUsSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    ref?.current?.querySelectorAll(".reveal, .reveal-scale")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="py-28 px-6 lg:px-10 bg-white">
      <div className="max-w-site mx-auto">
        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {metrics?.map((m, i) =>
          <div
            key={m?.label}
            className={`reveal delay-${i + 1} text-center p-6 rounded-card bg-parchment border border-crimson-100 hover:border-crimson/25 transition-all duration-400 hover:shadow-crimson-sm group`}>
            
              <p className="stat-number group-hover:scale-105 transition-transform inline-block">{m?.value}</p>
              <p className="font-serif font-semibold text-ink mt-2 text-base">{m?.label}</p>
              <p className="text-xs text-ink-muted mt-1">{m?.sub}</p>
            </div>
          )}
        </div>

        {/* Split layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: image collage */}
          <div className="relative h-[560px] reveal-scale delay-1">
            {/* Main image */}
            <div className="absolute top-0 left-0 w-[72%] h-[68%] rounded-[20px] overflow-hidden shadow-soft-lg">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1740d3e6d-1772815723825.png"
                alt="Professional education counsellor consulting with a student about overseas university options"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 40vw" />
              
              <div className="absolute inset-0 bg-gradient-to-tr from-crimson/20 to-transparent" />
            </div>
            {/* Secondary image */}
            <div className="absolute bottom-0 right-0 w-[55%] h-[48%] rounded-[16px] overflow-hidden shadow-crimson-md border-4 border-white">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1491c4e7a-1772815723752.png"
                alt="Happy Indian student group studying together at a university library abroad"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 50vw, 25vw" />
              
            </div>
            {/* Third image - placed in front of Partnerships/6+ Years area */}
            <div className="absolute bottom-[10%] left-0 w-[44%] h-[36%] rounded-[14px] overflow-hidden shadow-soft border-4 border-white z-10">
              <AppImage
                src="/assets/images/our-story-students.png"
                alt="Diverse group of international students collaborating and celebrating at a university campus"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 44vw, 22vw" />
            </div>
            {/* Floating badge */}
            <div
              className="absolute top-[36%] right-0 glass-card rounded-[14px] px-4 py-3 float-card-alt"
              style={{ animationDelay: "1s" }}>
              
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center text-sm">⭐</div>
                <div>
                  <p className="text-xs font-bold text-ink">4.9/5 Rating</p>
                  <p className="text-xs text-ink-muted">Google Reviews</p>
                </div>
              </div>
            </div>
            {/* Year badge */}
            <div className="absolute -top-4 right-[30%] w-20 h-20 rounded-full crimson-card flex flex-col items-center justify-center text-white float-card">
              <p className="font-serif font-bold text-xl leading-none">6+</p>
              <p className="text-xs opacity-80 mt-0.5">Years</p>
            </div>
          </div>

          {/* Right: content */}
          <div>
            <div className="reveal mb-4">
              <span className="section-label">Our Story</span>
            </div>
            <h2
              className="font-serif reveal delay-1 mb-4"
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.75rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
              
              We Built Global Varsity Because{" "}
              <span className="gradient-text italic">Students Deserved Better.</span>
            </h2>
            <p className="text-ink-muted leading-relaxed mb-4 reveal delay-2">
              When we started Global Varsity, the overseas education space was full of consultants who were more interested in commissions than outcomes. Students were being pushed toward the wrong universities, misled about costs, and left stranded when visas were rejected.
            </p>
            <p className="text-ink-muted leading-relaxed mb-8 reveal delay-2">
              Global Varsity was founded on one belief: every student deserves guidance that puts their future first — not an agent's bottom line. Over the past 6+ years, we've placed 500+ students across 15+ countries and earned a reputation we protect fiercely — because it was built one student at a time.
            </p>

            <div className="grid grid-cols-1 gap-5">
              {differentiators?.map((d, i) =>
              <div
                key={d?.title}
                className={`reveal delay-${i + 2} flex gap-4 p-4 rounded-[14px] bg-parchment border border-crimson-50 hover:border-crimson/20 transition-all duration-300 group hover:shadow-soft`}>
                
                  <div className="w-10 h-10 rounded-[10px] bg-crimson/8 flex items-center justify-center text-xl flex-shrink-0 group-hover:scale-110 transition-transform">
                    {d?.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-ink mb-1 text-sm">{d?.title}</h4>
                    <p className="text-xs text-ink-muted leading-relaxed">{d?.desc}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}